/**
 * Relevance Indicator
 *
 * A segmented bar (battery-style) indicator for displaying relevance/similarity scores.
 * Uses 10 blocks where each block represents 10% of relevance.
 *
 * Color coding (GO brand aligned):
 * - High (70%+): Teal - strong match (positive)
 * - Medium (40-69%): Primary - moderate match
 * - Low (<40%): Muted primary - weak match
 */

import React from 'react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

export interface RelevanceIndicatorProps {
  /** Relevance score as a decimal (0-1) or percentage (0-100) */
  value: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the percentage label */
  showLabel?: boolean;
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const BLOCK_COUNT = 10;

/**
 * Get the number of filled blocks based on percentage
 */
const getFilledBlocks = (percentage: number): number => {
  // Round to nearest 10, but ensure at least 1 block for values > 0
  if (percentage <= 0) return 0;
  if (percentage < 10) return 1;
  return Math.round(percentage / 10);
};

/**
 * Get color classes for blocks
 * Uses consistent primary color - the bar length communicates relevance level
 */
const getColorClasses = (isFilled: boolean): string => {
  if (!isFilled) {
    return 'bg-muted';
  }
  return 'bg-primary';
};

/**
 * Size configurations - slightly larger blocks with better gaps
 */
const sizeConfig = {
  sm: {
    block: 'w-[5px] h-3',
    gap: 'gap-[2px]',
    label: 'text-[10px]',
  },
  md: {
    block: 'w-[6px] h-4',
    gap: 'gap-[3px]',
    label: 'text-xs',
  },
  lg: {
    block: 'w-2 h-5',
    gap: 'gap-1',
    label: 'text-sm',
  },
};

/**
 * Relevance Indicator Component
 */
export const RelevanceIndicator: React.FC<RelevanceIndicatorProps> = ({
  value,
  size = 'sm',
  showLabel = true,
  showTooltip = true,
  className,
}) => {
  // Normalize value to percentage (0-100)
  const percentage = value > 1 ? value : value * 100;
  const filledBlocks = getFilledBlocks(percentage);
  const config = sizeConfig[size];

  const indicator = (
    <div className={cn('flex flex-col items-end', className)}>
      {/* Segmented bar */}
      <div className={cn('flex', config.gap)}>
        {Array.from({ length: BLOCK_COUNT }).map((_, index) => {
          const isFilled = index < filledBlocks;
          return (
            <div
              key={index}
              className={cn(
                config.block,
                'rounded-[2px] transition-colors',
                getColorClasses(isFilled)
              )}
            />
          );
        })}
      </div>

      {/* Label */}
      {showLabel && (
        <span
          className={cn(
            config.label,
            'text-muted-foreground mt-0.5 tabular-nums'
          )}
        >
          {Math.round(percentage)}% match
        </span>
      )}
    </div>
  );

  if (!showTooltip) {
    return indicator;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">{indicator}</div>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        {Math.round(percentage)}% relevance match
      </TooltipContent>
    </Tooltip>
  );
};

export default RelevanceIndicator;
